import { useState, useRef, useMemo } from "react";
import {
  IonPage,
  IonContent,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonFab,
  IonFabButton,
  IonModal,
} from "@ionic/react";
import { Plus, ChevronDown, ChevronUp, Sun, Trash2, Check, Link2 } from "lucide-react";
import { useStore } from "../store/useStore";
import GroupedTaskList from "../components/GroupedTaskList";
import TagFilterPills from "../components/TagFilterPills";
import { deduplicateTasks, filterByTag, sortTasks } from "../lib/task-filters";
import { parseMultiLineInput } from "../lib/parse-input";
import { useConfirmedComplete } from "../hooks/useConfirmedComplete";
import AddPrerequisiteModal from "../components/AddPrerequisiteModal";

export default function BrainDumpPage() {
  const [input, setInput] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [selectedAssignee, setSelectedAssignee] = useState<string | null>(null);
  const [selectedTagId, setSelectedTagId] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const tasks = useStore((s) => s.tasks);
  const sharedTasks = useStore((s) => s.sharedTasks);
  const createTask = useStore((s) => s.createTask);
  const createSharedTask = useStore((s) => s.createSharedTask);
  const moveToToday = useStore((s) => s.moveToToday);
  const deleteTask = useStore((s) => s.deleteTask);
  const { requestComplete, modal: completionModal } = useConfirmedComplete();
  const [prereqTarget, setPrereqTarget] = useState<string | null>(null);
  const loading = useStore((s) => s.loading.tasks);
  const tags = useStore((s) => s.tags);
  const sharedTags = useStore((s) => s.sharedTags);
  const activeTagFilter = useStore((s) => s.activeTagFilter);
  const activeSortFilter = useStore((s) => s.activeSortFilter);

  const activeTasks = useMemo(() => {
    const ownActive = tasks.filter((t) => t.status === "backlog" || t.status === "today");
    const sharedActive = sharedTasks.filter((t) => t.status === "backlog" || t.status === "today");
    const filtered = filterByTag(
      deduplicateTasks([...ownActive, ...sharedActive]),
      activeTagFilter
    );
    return sortTasks(filtered, activeSortFilter);
  }, [tasks, sharedTasks, activeTagFilter, activeSortFilter]);

  const parsedItems = useMemo(() => parseMultiLineInput(input), [input]);
  const isMultiTask = parsedItems.length > 1;

  const selectedSharedTag = selectedAssignee
    ? sharedTags.find((st) => st.owner_uuid === selectedAssignee)
    : null;

  const handleSubmit = async () => {
    const trimmed = input.trim();
    if (!trimmed || isSubmitting) return;

    setIsSubmitting(true);
    setInput("");

    if (selectedAssignee && selectedSharedTag) {
      await createSharedTask(trimmed, selectedSharedTag.tag_id);
    } else {
      // Always send full input as a single task — AI will split into
      // multiple tasks if needed, preserving shared context across all of them.
      const item = parsedItems[0];
      await createTask(
        trimmed,
        isMultiTask ? undefined : (item?.url ?? undefined),
        selectedTagId ?? undefined,
      );
    }

    setIsSubmitting(false);
    setSheetOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      if (isMultiTask) return;
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Brain Dump</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div className="px-4 pt-3 pb-2 space-y-3">
          <TagFilterPills />

          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-20 shimmer rounded-lg" />
              ))}
            </div>
          ) : activeTasks.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground text-lg">No tasks yet</p>
              <p className="text-muted-foreground text-sm mt-1">
                Tap + to dump what&apos;s on your mind
              </p>
            </div>
          ) : (
            <GroupedTaskList
              tasks={activeTasks}
              showMoveToToday
              showStatus
              swipeStart={(task) =>
                task.status === "backlog"
                  ? {
                      label: "Today",
                      icon: <Sun className="w-5 h-5" />,
                      bg: "hsl(var(--success))",
                      onAction: (uuid) => moveToToday(uuid),
                    }
                  : {
                      label: "Done",
                      icon: <Check className="w-5 h-5" />,
                      bg: "hsl(var(--success))",
                      onAction: (uuid) => requestComplete(uuid),
                    }
              }
              swipeEnd={{
                label: "Delete",
                icon: <Trash2 className="w-5 h-5" />,
                bg: "hsl(var(--destructive))",
                onAction: (uuid) => deleteTask(uuid),
              }}
              swipeEndSecondary={{
                label: "Prereq",
                icon: <Link2 className="w-5 h-5" />,
                bg: "hsl(var(--primary))",
                onAction: (uuid) => setPrereqTarget(uuid),
              }}
            />
          )}
        </div>

        {/* FAB */}
        <IonFab vertical="bottom" horizontal="end" slot="fixed">
          <IonFabButton onClick={() => setSheetOpen(true)}>
            <Plus className="w-6 h-6" />
          </IonFabButton>
        </IonFab>

        {completionModal}

        {prereqTarget &&
          (() => {
            const target = tasks.find((t) => t.uuid === prereqTarget);
            if (!target) return null;
            return (
              <AddPrerequisiteModal
                isOpen={!!prereqTarget}
                onClose={() => setPrereqTarget(null)}
                dependentTask={target}
              />
            );
          })()}

        {/* Bottom sheet for task creation */}
        <IonModal
          isOpen={sheetOpen}
          onDidDismiss={() => setSheetOpen(false)}
          initialBreakpoint={0.45}
          breakpoints={[0, 0.45, 0.75]}
          onDidPresent={() => textareaRef.current?.focus()}
        >
          <div className="p-5 space-y-3">
            <h2 className="text-base font-semibold text-foreground">New Task</h2>

            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="What's on your mind?"
              rows={3}
              className="w-full resize-none rounded-lg border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />

            {isMultiTask && (
              <p className="text-xs text-muted-foreground">
                AI will split into separate tasks
              </p>
            )}

            {/* Advanced section */}
            {(sharedTags.length > 0 || tags.length > 0) && (
              <div>
                <button
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showAdvanced ? (
                    <ChevronUp className="w-3 h-3" />
                  ) : (
                    <ChevronDown className="w-3 h-3" />
                  )}
                  Advanced
                </button>

                {showAdvanced && (
                  <div className="flex gap-3 mt-2">
                    {sharedTags.length > 0 && (
                      <div className="flex-1">
                        <label className="text-xs text-muted-foreground">Assign to</label>
                        <select
                          value={selectedAssignee || ""}
                          onChange={(e) => {
                            const val = e.target.value || null;
                            setSelectedAssignee(val);
                            if (val) setSelectedTagId(null);
                          }}
                          className="mt-0.5 w-full rounded border border-input bg-background px-2 py-1.5 text-sm"
                        >
                          <option value="">Me</option>
                          {sharedTags.map((st) => (
                            <option key={st.owner_uuid} value={st.owner_uuid}>
                              {st.owner_name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {!selectedAssignee && tags.length > 0 && (
                      <div className="flex-1">
                        <label htmlFor="brain-dump-tag" className="text-xs text-muted-foreground">Tag</label>
                        <select
                          id="brain-dump-tag"
                          value={selectedTagId || ""}
                          onChange={(e) => setSelectedTagId(e.target.value || null)}
                          className="mt-0.5 w-full rounded border border-input bg-background px-2 py-1.5 text-sm"
                        >
                          <option value="">None (AI will tag)</option>
                          {tags.map((tag) => (
                            <option key={tag.id} value={tag.id}>
                              {tag.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {selectedAssignee && selectedSharedTag && (
                      <div className="flex-1">
                        <label className="text-xs text-muted-foreground">Tag</label>
                        <div className="mt-0.5 flex items-center gap-1.5 px-2 py-1.5 text-sm text-muted-foreground bg-muted rounded">
                          <span
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: selectedSharedTag.tag_color }}
                          />
                          {selectedSharedTag.tag_name}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <button
              onClick={handleSubmit}
              disabled={!input.trim() || isSubmitting}
              className="btn-primary w-full flex items-center justify-center gap-1.5"
            >
              <Plus className="w-4 h-4" />
              {isSubmitting ? "Adding..." : "Add"}
            </button>
          </div>
        </IonModal>
      </IonContent>
    </IonPage>
  );
}
